import { useNavigate } from "react-router-dom";
import { Zap, FileText, Mail, TrendingUp, Sparkles, ArrowRight, Check } from "lucide-react";

const FEATURES = [
  { icon: FileText, title: "Blog Posts", desc: "SEO-optimized articles that rank and convert" },
  { icon: Mail, title: "Email Copy", desc: "High-converting email sequences in seconds" },
  { icon: TrendingUp, title: "Marketing Copy", desc: "Ad copy, landing pages, and sales letters" },
  { icon: Sparkles, title: "Social Media", desc: "Scroll-stopping posts for every platform" },
];

const PRICING = [
  { name: "Free", price: "$0", period: "forever", credits: "10 credits", features: ["All templates", "Basic output", "Content history"], cta: "Start Free", highlight: false },
  { name: "Starter", price: "$9", period: "/mo", credits: "100 credits", features: ["All templates", "Advanced output", "Priority generation", "Export options"], cta: "Get Started", highlight: false },
  { name: "Pro", price: "$29", period: "/mo", credits: "500 credits", features: ["All templates", "Premium output", "Priority generation", "API access", "Bulk generation"], cta: "Go Pro", highlight: true },
  { name: "Enterprise", price: "$79", period: "/mo", credits: "2000 credits", features: ["Everything in Pro", "Custom templates", "Dedicated support", "Team features", "White-label"], cta: "Scale Up", highlight: false },
];

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-heavy" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")} data-testid="nav-logo">
            <Zap className="w-6 h-6 text-violet-400" />
            <span className="font-semibold text-lg tracking-tight" style={{ fontFamily: 'Outfit' }}>ContentForge</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/auth")} className="text-sm text-gray-300 hover:text-white px-4 py-2 transition-colors duration-200" data-testid="nav-login-btn">
              Log in
            </button>
            <button onClick={() => navigate("/auth")} className="text-xs font-medium tracking-wide uppercase bg-violet-600 hover:bg-violet-500 text-white px-5 py-2.5 rounded-lg transition-colors duration-200 shadow-[0_0_20px_rgba(139,92,246,0.3)]" data-testid="nav-signup-btn">
              Start Free
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-24 md:pt-44 md:pb-36 px-6 md:px-12 max-w-7xl mx-auto relative" data-testid="hero-section">
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-violet-600/10 rounded-full blur-[120px] pointer-events-none" />
        <div className="relative z-10 max-w-4xl">
          <div className="animate-fade-in-up">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium tracking-wide uppercase bg-violet-500/10 border border-violet-500/20 text-violet-300 mb-8">
              <Sparkles className="w-3.5 h-3.5" /> Powered by GPT-5.2
            </span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-[1.08] mb-8 animate-fade-in-up-delay-1" style={{ fontFamily: 'Outfit' }}>
            <span className="gradient-text">Content that</span>
            <br />
            <span className="brand-gradient-text">converts & sells</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-400 max-w-2xl mb-12 leading-relaxed animate-fade-in-up-delay-2" style={{ fontFamily: 'Manrope' }}>
            Generate blog posts, marketing copy, emails, and social media content in seconds. 
            Built for creators, marketers, and businesses that move fast.
          </p>
          <div className="flex flex-wrap gap-4 animate-fade-in-up-delay-3">
            <button 
              onClick={() => navigate("/auth")} 
              className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white px-8 py-3.5 rounded-lg font-medium transition-all duration-200 shadow-[0_0_30px_rgba(139,92,246,0.4)] hover:shadow-[0_0_40px_rgba(139,92,246,0.6)] active:scale-95"
              data-testid="hero-cta-btn"
            >
              Start generating free <ArrowRight className="w-4 h-4" />
            </button>
            <button 
              onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center gap-2 border border-white/10 hover:bg-white/5 text-white px-8 py-3.5 rounded-lg font-medium transition-colors duration-200"
              data-testid="hero-pricing-btn"
            >
              View pricing
            </button>
          </div>
          <div className="mt-12 flex items-center gap-6 text-sm text-gray-500 animate-fade-in-up-delay-4">
            <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-400" /> 10 free credits</span>
            <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-400" /> No card required</span>
            <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-400" /> Cancel anytime</span>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 md:py-32 px-6 md:px-12 max-w-7xl mx-auto" data-testid="features-section">
        <div className="mb-16">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Outfit' }}>
            <span className="gradient-text">Every content type.</span><br />
            <span className="text-gray-500">One platform.</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {FEATURES.map((f, i) => (
            <div key={i} className="group template-card bg-gray-900/50 border border-white/5 rounded-xl p-6 hover:border-violet-500/30 cursor-pointer" data-testid={`feature-card-${i}`}>
              <div className="w-10 h-10 rounded-lg bg-violet-500/10 flex items-center justify-center mb-4 group-hover:bg-violet-500/20 transition-colors duration-300">
                <f.icon className="w-5 h-5 text-violet-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit' }}>{f.title}</h3>
              <p className="text-sm text-gray-400 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-16 px-6 md:px-12 max-w-7xl mx-auto border-t border-white/5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { value: "50K+", label: "Content pieces generated" },
            { value: "8,000+", label: "Active creators" },
            { value: "4.9/5", label: "User rating" },
            { value: "<3s", label: "Average generation time" },
          ].map((s, i) => (
            <div key={i} data-testid={`stat-${i}`}>
              <div className="text-3xl md:text-4xl font-bold brand-gradient-text mb-1" style={{ fontFamily: 'Outfit' }}>{s.value}</div>
              <div className="text-sm text-gray-500">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 md:py-32 px-6 md:px-12 max-w-7xl mx-auto" data-testid="pricing-section">
        <div className="mb-16 text-center">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Outfit' }}>
            <span className="gradient-text">Simple pricing.</span>
          </h2>
          <p className="text-gray-400 text-lg">Start free. Scale as you grow.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {PRICING.map((plan, i) => (
            <div key={i} className={`relative bg-gray-900/50 border rounded-xl p-6 flex flex-col ${plan.highlight ? 'border-violet-500/50 shadow-[0_0_30px_rgba(139,92,246,0.15)]' : 'border-white/5'}`} data-testid={`pricing-card-${plan.name.toLowerCase()}`}>
              {plan.highlight && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-medium tracking-wide uppercase bg-violet-600 text-white px-3 py-1 rounded-full">Popular</span>
              )}
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-1" style={{ fontFamily: 'Outfit' }}>{plan.name}</h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold" style={{ fontFamily: 'Outfit' }}>{plan.price}</span>
                  <span className="text-gray-500 text-sm">{plan.period}</span>
                </div>
                <p className="text-sm text-violet-400 mt-1">{plan.credits}</p>
              </div>
              <ul className="space-y-2.5 mb-8 flex-1">
                {plan.features.map((feat, fi) => (
                  <li key={fi} className="flex items-center gap-2 text-sm text-gray-300">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0" /> {feat}
                  </li>
                ))}
              </ul>
              <button 
                onClick={() => navigate("/auth")} 
                className={`w-full py-2.5 rounded-lg text-sm font-medium tracking-wide uppercase transition-all duration-200 active:scale-95 ${plan.highlight ? 'bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white shadow-[0_0_20px_rgba(139,92,246,0.3)]' : 'border border-white/10 hover:bg-white/5 text-white'}`}
                data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
              >
                {plan.cta}
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 md:px-12 max-w-7xl mx-auto text-center">
        <div className="relative bg-gray-900/50 border border-white/5 rounded-2xl p-12 md:p-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-violet-600/5 to-cyan-600/5" />
          <div className="relative z-10">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Outfit' }}>
              <span className="gradient-text">Ready to forge</span><br />
              <span className="brand-gradient-text">your content?</span>
            </h2>
            <p className="text-gray-400 text-lg mb-8 max-w-xl mx-auto">Join thousands of creators generating high-converting content in seconds.</p>
            <button onClick={() => navigate("/auth")} className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white px-8 py-3.5 rounded-lg font-medium transition-all duration-200 shadow-[0_0_30px_rgba(139,92,246,0.4)] active:scale-95" data-testid="cta-signup-btn">
              Get started free <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8 px-6 md:px-12 max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-violet-400" />
            <span className="font-semibold tracking-tight" style={{ fontFamily: 'Outfit' }}>ContentForge AI</span>
          </div>
          <p className="text-sm text-gray-500">Built with AI. Powered by GPT-5.2.</p>
        </div>
      </footer>
    </div>
  );
}
